import { useQuery, useQueryClient } from "@tanstack/react-query"

import { isAllBooksId } from "@/lib/all-books"
import { useFetchFeatureFlags } from "@/hooks/api/feature-flags/queries"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import { fetchSlantaAvailability, fetchSlantaHouseholds, fetchSlantaOrders } from "./api"
import { SlantaOrder } from "./types"

export function useSlantaAvailability() {
  const getContext = useApiContext()
  const bookId = useBookId()
  const { data: featureFlags } = useFetchFeatureFlags()
  return useQuery({
    queryKey: ["slanta", "availability", bookId],
    enabled: !!bookId && !isAllBooksId(bookId) && !!featureFlags && !featureFlags.includes("slanta_disabled"),
    staleTime: 30_000,
    refetchInterval: (query) => (query.state.data?.submission_open ? 30_000 : false),
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchSlantaAvailability(context)
    },
  })
}

export function useSlantaOrders() {
  const queryClient = useQueryClient()
  const getContext = useApiContext()
  const bookId = useBookId()
  return useQuery({
    queryKey: ["slanta", "orders", bookId],
    refetchInterval: (query) =>
      query.state.data?.orders.some(
        (order) =>
          order.status === "payment_pending" ||
          ["queued", "writing"].includes(order.share_posts_status) ||
          order.gifts.some((gift) => ["queued", "writing"].includes(gift.card_draft_status))
      )
        ? 1500
        : false,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      const result = await fetchSlantaOrders(context)
      const current = queryClient.getQueryData<{ orders: SlantaOrder[] }>(["slanta", "orders", bookId])
      // A card may finish while this order snapshot is in flight.
      const currentGifts = new Map(
        current?.orders.flatMap((order) => order.gifts.map((gift) => [gift.id, gift] as const))
      )
      return {
        orders: result.orders.map((order) => ({
          ...order,
          gifts: order.gifts.map((gift) => {
            const existing = currentGifts.get(gift.id)
            return existing && existing.updated_at > gift.updated_at ? existing : gift
          }),
        })),
      }
    },
  })
}

export function useSlantaHouseholds(enabled = true) {
  const getContext = useApiContext()
  const bookId = useBookId()
  return useQuery({
    queryKey: ["slanta", "households", bookId],
    enabled,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchSlantaHouseholds(context)
    },
  })
}
